import { serviceWorkerRegistration } from '../registerWorkers';
import { deleteApp, type FirebaseApp, initializeApp } from '@firebase/app';
import { getMessaging, isSupported } from '@firebase/messaging';
import lazy from '@whiz-cart/node-shared/lazy';
import { v4 as guid } from 'uuid';

declare global {
    interface Window {
        __firebase?: FirebaseApp;
    }
}

if (import.meta.hot && window.__firebase) {
    void deleteApp(window.__firebase);
}

const app = config?.firebase ? initializeApp(config.firebase, guid()) : undefined;

if (import.meta.hot) {
    window.__firebase = app;
}

/** Resolves to undefined when there is no firebase config or the browser lacks what FCM needs. */
export const firebaseMessaging = lazy(async () => {
    if (!app || !(await isSupported())) {
        return undefined;
    }

    await deleteDatabasesDamagedByLocalforage();

    const sw = await serviceWorkerRegistration;

    if (!sw) {
        throw new Error('Service worker not registered');
    }

    // Without a known registration, deleteToken registers firebase's default /firebase-messaging-sw.js.
    // The SDK dropped its public setter (useServiceWorker), so set the field getToken would set.
    return Object.assign(getMessaging(app), { swRegistration: sw });
});

// Earlier versions cleared Firebase's databases through localforage, which adds this store and bumps the
// version. Firebase then fails to open them (VersionError) and getToken never succeeds until they are deleted.
const localforageMarkerStore = 'local-forage-detect-blob-support';

async function deleteDatabasesDamagedByLocalforage() {
    const databases = (await indexedDB.databases?.()) ?? [];

    for (const { name } of databases) {
        if (!name?.startsWith('firebase-')) continue;

        const damaged = await new Promise<boolean>((resolve) => {
            const request = indexedDB.open(name);
            request.onsuccess = () => {
                const db = request.result;
                resolve(db.objectStoreNames.contains(localforageMarkerStore));
                db.close();
            };
            request.onerror = () => resolve(false);
        });

        if (damaged) {
            console.warn(`Deleting ${name}, damaged by localforage`);
            await new Promise<void>((resolve) => {
                const request = indexedDB.deleteDatabase(name);
                request.onsuccess = request.onerror = request.onblocked = () => resolve();
            });
        }
    }
}
